import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import ListCardOrganization from "go-app/components/Organizations/ListCardOrganization";
import ListCardOrganizationInvitation from "go-app/components/Organizations/ListCardOrganizationInvitation";
import handleError from "go-app/services/errors";
import { OrganizationCard, OrganizationCardError } from "go-app/services/types";
import { useOrganizations } from "go-app/utils/OrganizationsContext";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import EmptyData from "go-core/components/EmptyData";
import { LoadingContainer } from "go-core/components/Loading";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { listFiltersEncode } from "go-list/core/components/Filter/services/decoder";
import { InvitationApi } from "../../../../../../services/Api/Organization/organizationTypes";
import { partnerApi } from "../../../../../../services/Api/Partner/partnerApi";
import { PartnerApi } from "../../../../../../services/Api/Partner/partnerTypes";
import { userApi } from "../../../../../../services/Api/User/userApi";
import { ConfigContext } from "../../../../../../services/Config/context";
import { api } from "../../../../../../services/Security/api";
import { useAuth } from "../../../../../../services/Security/auth";
import { getLicenseAlertText } from "../../../../../../services/license";
import UserDashboardHeader from "./components/UserDashboardHeader";

const UserDashboardPage = () => {
	const [invitations, setInvitations] = useState<InvitationApi[]>([]);
	const { addFlash, addSuccessFlash } = useFlash();
	const auth = useAuth();
	const confirmation = useConfirmation();
	const configService = useContext(ConfigContext);
	const applications = configService.applications();
	const { organizations, setOrganizations } = useOrganizations();
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState("");
	const { t } = useTranslation();
	const [showNewOrganizationModal, setShowNewOrganizationModal] = useState<boolean>(false);
	const [partners, setPartners] = useState<PartnerApi[]>([]);
	const location = useLocation();
	const { handleChangeTabTitle } = useBrowserTabTitle();

	const fetchOrganizations = async (search: string) => {
		const res = await userApi.getMeOrganizations({ include: "applications,place", search });
		setOrganizations(res.data.data);
		setLoading(false);
	};

	useEffect(() => {
		handleSearch();
	}, [search]);

	const handleSearch = async () => {
		handleChangeTabTitle(t("lib:common.word.locations"));
		await fetchOrganizations(search);
		const filters = listFiltersEncode([
			{
				filterId: "status",
				condition: "e",
				value: "PENDING",
			},
		]);
		const params = {
			include: "organization,organization.info,place",
			f: filters,
		};

		try {
			const res = await api.user().getMeInvitations(params);
			setInvitations(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	useEffect(() => {
		handleSetModalOpenState();
	}, [location.search]);

	const getPartners = async () => {
		try {
			const res = await partnerApi.getPartners({ include: "parent" });
			setPartners(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleSetModalOpenState = async () => {
		const urlParams = new URLSearchParams(location.search);

		if (urlParams.get("action") === "add") {
			await getPartners();
			setShowNewOrganizationModal(true);
		}
	};

	const getFilteredInvitations = () => {
		return invitations.filter((invitation) => {
			const organization = invitation.organization;
			return organization.name.toLowerCase().includes(search.toLowerCase());
		});
	};

	const handleAcceptInvitation = async (id: string) => {
		const invitationId = Number(id);
		try {
			await api.user().acceptMeInvitation(invitationId);
			setInvitations([...invitations.filter((f) => f.id !== invitationId)]);
			await auth.checkLogin();
			addSuccessFlash(t("lib:go_security.flash.invitation_accepted"));
			await fetchOrganizations(search);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleRejectInvitation = async (id: string) => {
		try {
			await confirmation({
				title: t("lib:common.word.confirmation"),
				message: t("lib:go_security.invitation.reject.confirmation.message"),
			});
			const invitationId = Number(id);
			await api.user().rejectMeInvitation(invitationId);
			setInvitations([...invitations.filter((f) => f.id !== invitationId)]);
			await auth.checkLogin();
			addSuccessFlash(t("lib:go_security.flash.invitation_rejected"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const hasAcceptedTerms = (acceptPrivacy: boolean, acceptRules: boolean, acceptTerms: boolean) => {
		return !acceptPrivacy || !acceptRules || !acceptTerms;
	};

	const getErrorsForOrganization = (organization: Record<string, any>) => {
		const licenseError = {
			error: getLicenseAlertText(
				t,
				organization?.license_expires_at,
				organization?.license_type,
				organization.timezone
			),
			link: `/${organization.id}/subscriptions`,
		};
		const acceptedTermsError = {
			error:
				hasAcceptedTerms(organization.accept_privacy, organization.accept_rules, organization.accept_terms) &&
				t("modules.dashboard.field.not_accepted_terms.title"),
		};

		const errors: OrganizationCardError[] = [];

		licenseError && errors.push(licenseError);
		acceptedTermsError && errors.push(acceptedTermsError as OrganizationCardError);

		return errors;
	};

	const viewOrganizations = organizations.map((x) => {
		return {
			id: x.id?.toString(),
			name: x.name,
			company_id: x.company_id,
			created_at: x.created_at,
			applications: x.applications.map((y) => y.slug),
			place: x?.place,
			errors: getErrorsForOrganization(x),
		} as OrganizationCard;
	});
	const installedOrganizations = viewOrganizations.filter((x) => x.applications.length > 0);
	const notInstalledOrganizations = viewOrganizations.filter((x) => x.applications.length <= 0);
	const viewInvitations = getFilteredInvitations().map((x) => {
		return {
			organization: {
				id: x.id?.toString(),
				name: x.organization.name,
				company_id: x.organization.company_id,
				place: x.organization?.place,
				created_at: x.created_at,
				applications: [],
			} as OrganizationCard,
			invitation_id: x.id,
		};
	});

	if (loading) return <LoadingContainer />;

	return (
		<>
			<div className="content">
				<UserDashboardHeader
					setSearch={setSearch}
					partners={partners}
					setPartners={setPartners}
					setShowNewOrganizationModal={setShowNewOrganizationModal}
					showNewOrganizationModal={showNewOrganizationModal}
				/>
				{(!organizations || organizations.length === 0) &&
				(!getFilteredInvitations() || getFilteredInvitations().length === 0) ? (
					<>
						<EmptyData
							title={
								!search
									? t("modules.dashboard.field.no_organizations.title")
									: t("modules.dashboard.field.no_organizations_found.title")
							}
						/>
					</>
				) : (
					<>
						{viewInvitations !== undefined && viewInvitations.length > 0 && (
							<>
								<h5>{t("lib:go_security.user.dashboard.header.your_invitations")}</h5>
								<div className="row row-cols-1 rows-cols-md-2 row-cols-lg-3 row-cols-xl-4">
									{viewInvitations.map((invitation) => {
										return (
											<ListCardOrganizationInvitation
												key={invitation.invitation_id}
												organization={invitation.organization}
												id={invitation.invitation_id.toString()}
												onAccept={handleAcceptInvitation}
												onReject={handleRejectInvitation}
											/>
										);
									})}
								</div>
								{organizations.length > 0 && (
									<div>
										<hr className={"mb-4"} />
									</div>
								)}
							</>
						)}
						{organizations.length > 0 && (
							<>
								<h5>{t("modules.dashboard.field.your_organizations.title")}</h5>
								<div className="row row-cols-1 rows-cols-md-2 row-cols-lg-3 row-cols-xl-4">
									{installedOrganizations.map((organization) => (
										<ListCardOrganization
											key={organization.company_id}
											organization={organization}
											applications={applications}
											oldVersionAppsTimestamp={configService.getAppsOldVersionTimestamps()}
										/>
									))}
								</div>
								<div>
									<hr className={"mb-4"} />
								</div>
								<div className="row row-cols-1 rows-cols-md-2 row-cols-lg-3 row-cols-xl-4">
									{notInstalledOrganizations.map((organization) => (
										<ListCardOrganization
											key={organization.company_id}
											organization={organization}
											applications={applications}
											oldVersionAppsTimestamp={configService.getAppsOldVersionTimestamps()}
										/>
									))}
								</div>
							</>
						)}
					</>
				)}
			</div>
		</>
	);
};

export default UserDashboardPage;
